/**
 * Compact inline form to create an activity without leaving the parent form.
 */

import * as React from 'react';
import { Input } from '@/components/forms/Input';
import { Select, SelectOption } from '@/components/forms/Select';
import { Button } from '@/components/forms/Button';
import { X, Plus, Check } from 'lucide-react';
import type { UseFormValidationReturn } from '@/hooks/useFormValidation';
import type { CreateActivityDto } from '@/types/activity.types';

/** Props for {@link InlineActivityForm}. */
interface InlineActivityFormProps {
  newActivityData: Partial<CreateActivityDto>;
  setNewActivityData: React.Dispatch<React.SetStateAction<Partial<CreateActivityDto>>>;
  showActivityTypeForm: boolean;
  setShowActivityTypeForm: (show: boolean) => void;
  activityTypeOptions: SelectOption[];
  activityValidation: UseFormValidationReturn;
  onClose: () => void;
  onValidate: () => void;
}

/** Inline form to create or edit an activity. */
export const InlineActivityForm: React.FC<InlineActivityFormProps> = ({
  newActivityData,
  setNewActivityData,
  showActivityTypeForm,
  setShowActivityTypeForm,
  activityTypeOptions,
  activityValidation,
  onClose,
  onValidate,
}) => {
  const formRef = React.useRef<HTMLDivElement>(null);

  /** Scroll the inline form into view when it mounts. */
  React.useEffect(() => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, []);

  return (
    <div ref={formRef} className="form-section bg-[var(--color-1-nuance-4)] p-4 rounded-lg mt-4">
      <div className="flex items-center justify-between mb-4">
        <h5 className="font-semibold">Nouvelle activité</h5>
        <Button preset="form-close" onClick={onClose} />
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Code"
            placeholder="Ex: AIDE_DEVOIRS_6EME"
            value={newActivityData.code || ''}
            onChange={(e) => {
              setNewActivityData((prev) => ({ ...prev, code: e.target.value }));
              activityValidation.validateField('code', e.target.value, newActivityData);
            }}
            onBlur={() => activityValidation.markAsTouched('code')}
            required
            maxLength={50}
            helperText="Code unique identifiant l'activité"
            state={activityValidation.getFieldState('code')}
            error={activityValidation.getFieldError('code')}
            inputSize="sm"
          />
          <Input
            label="Libellé"
            placeholder="Ex: Aide aux devoirs niveau 6ème"
            value={newActivityData.label || ''}
            onChange={(e) => {
              setNewActivityData((prev) => ({ ...prev, label: e.target.value }));
              activityValidation.validateField('label', e.target.value, newActivityData);
            }}
            onBlur={() => activityValidation.markAsTouched('label')}
            required
            maxLength={255}
            helperText="Nom complet de l'activité"
            state={activityValidation.getFieldState('label')}
            error={activityValidation.getFieldError('label')}
            inputSize="sm"
          />
        </div>

        {!showActivityTypeForm ? (
          <div className="space-y-2">
            <Select
              label="Type d'activité"
              placeholder="Sélectionner un type"
              options={activityTypeOptions}
              value={newActivityData.activityTypeId || ''}
              onChange={(value) => {
                setNewActivityData((prev) => ({ ...prev, activityTypeId: value }));
                activityValidation.validateField('activityTypeId', value, newActivityData);
              }}
              onBlur={() => activityValidation.markAsTouched('activityTypeId')}
              required
              size="sm"
              state={activityValidation.getFieldState('activityTypeId')}
              error={activityValidation.getFieldError('activityTypeId')}
            />
            <Button
              variant="outline"
              size="sm"
              icon={Plus}
              iconPosition="left"
              onClick={() => setShowActivityTypeForm(true)}
            >
              Créer un nouveau type
            </Button>
          </div>
        ) : null}

        <Input
          label="Description"
          placeholder="Ex: Accompagnement scolaire"
          value={newActivityData.description || ''}
          onChange={(e) => {
            setNewActivityData((prev) => ({ ...prev, description: e.target.value }));
            activityValidation.validateField('description', e.target.value, newActivityData);
          }}
          onBlur={() => activityValidation.markAsTouched('description')}
          maxLength={500}
          state={activityValidation.getFieldState('description')}
          error={activityValidation.getFieldError('description')}
          inputSize="sm"
        />
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline" size="sm" onClick={onClose}>
          <X className="w-4 h-4 mr-2" />
          Annuler
        </Button>
        <Button variant="primary" size="sm" onClick={onValidate}>
          <Check className="w-4 h-4 mr-2" />
          Valider
        </Button>
      </div>
    </div>
  );
};
